import { useState, useEffect, useMemo, useCallback } from "react";
import { ContainerCard } from "../../components/ui/cards";
import { cn } from "../../utils/tailwindUtils";
import {
    RefreshCw, Search, ChevronDown, Sparkles, Phone, CalendarClock, FileText,
    ArrowUpCircle, UserPlus, Leaf, XCircle, Check, X, Clipboard,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { AI_SUGGESTIONS_URL, AI_SUGGESTION_UPDATE, AI_BRIEFING_URL, USERS_SIMPLE_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { useNavigate } from "react-router-dom";
import { usePersistedState } from "../../hooks/usePersistedState";
import dayjs from "dayjs";
import TruncatedText from "../../components/ui/truncated-text";

// ── action metadata ───────────────────────────────────────────────────────────
// Icon + label + badge colour per action_type (mirrors LeadAISuggestion.ACTION_TYPES)

const ACTIONS = {
    call_now:        { label: "Call Now",        icon: Phone,         badge: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300" },
    reschedule_demo: { label: "Reschedule Demo", icon: CalendarClock, badge: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" },
    send_proposal:   { label: "Send Proposal",   icon: FileText,      badge: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
    escalate:        { label: "Escalate",        icon: ArrowUpCircle, badge: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300" },
    reassign:        { label: "Reassign",        icon: UserPlus,      badge: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300" },
    nurture:         { label: "Nurture",         icon: Leaf,          badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300" },
    drop:            { label: "Drop",            icon: XCircle,       badge: "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300" },
};

const STATUS_TABS = [
    { value: "pending",   label: "Pending" },
    { value: "accepted",  label: "Accepted" },
    { value: "done",      label: "Done" },
    { value: "dismissed", label: "Dismissed" },
    { value: "all",       label: "All" },
];

const priorityTone = (p) => {
    if (p >= 80) return "bg-rose-500";
    if (p >= 50) return "bg-amber-500";
    return "bg-gray-400";
};

const priorityLabel = (p) => (p >= 80 ? "Urgent" : p >= 50 ? "This week" : "Low");

// ── very small markdown renderer for the briefing ─────────────────────────────
// The briefing is model-authored markdown: headings, bold, tables, bullets, rules.
// Rendering it with a handful of rules keeps the bundle free of a markdown dep.

const inline = (text) =>
    text.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) =>
        chunk.startsWith("**") && chunk.endsWith("**")
            ? <strong key={i} className="font-semibold text-gray-900 dark:text-white">{chunk.slice(2, -2)}</strong>
            : <span key={i}>{chunk}</span>
    );

const Briefing = ({ md }) => {
    const blocks = useMemo(() => {
        const lines = (md || "").split("\n");
        const out = [];
        let table = null;

        const flushTable = () => {
            if (table && table.rows.length) out.push({ type: "table", ...table });
            table = null;
        };

        lines.forEach((raw) => {
            const line = raw.trim();
            const isTableRow = line.startsWith("|") && line.endsWith("|");

            if (isTableRow) {
                const cells = line.slice(1, -1).split("|").map(c => c.trim());
                // separator row (|---|---|) switches the buffer from head to body
                if (cells.every(c => /^:?-{2,}:?$/.test(c))) return;
                if (!table) table = { head: cells, rows: [] };
                else table.rows.push(cells);
                return;
            }
            flushTable();

            if (!line) return;
            if (/^-{3,}$/.test(line)) { out.push({ type: "rule" }); return; }
            const heading = line.match(/^(#{1,6})\s+(.*)$/);
            if (heading) { out.push({ type: "heading", level: heading[1].length, text: heading[2] }); return; }
            if (/^[-*]\s+/.test(line)) { out.push({ type: "li", text: line.replace(/^[-*]\s+/, "") }); return; }
            out.push({ type: "p", text: line });
        });
        flushTable();
        return out;
    }, [md]);

    return (
        <div className="space-y-2">
            {blocks.map((b, i) => {
                if (b.type === "rule") return <hr key={i} className="border-gray-200 dark:border-gray-700" />;
                if (b.type === "heading") return (
                    <div key={i} className={cn(
                        "font-semibold text-gray-900 dark:text-white",
                        b.level <= 1 ? "text-sm" : "text-xs uppercase tracking-wide text-gray-600 dark:text-gray-300"
                    )}>{inline(b.text)}</div>
                );
                if (b.type === "li") return (
                    <div key={i} className="flex gap-2 text-xs text-gray-600 dark:text-gray-300">
                        <span className="text-indigo-400 mt-0.5">•</span>
                        <span>{inline(b.text)}</span>
                    </div>
                );
                if (b.type === "table") return (
                    <div key={i} className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="w-full text-[11px]">
                            <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr>
                                    {b.head.map((h, j) => (
                                        <th key={j} className="px-2.5 py-1.5 text-left font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap">
                                            {inline(h)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {b.rows.map((r, j) => (
                                    <tr key={j}>
                                        {r.map((c, k) => (
                                            <td key={k} className="px-2.5 py-1.5 text-gray-600 dark:text-gray-300 align-top">{inline(c)}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
                return <p key={i} className="text-xs text-gray-600 dark:text-gray-300">{inline(b.text)}</p>;
            })}
        </div>
    );
};

// ── main component ────────────────────────────────────────────────────────────

const AISuggestions = () => {
    const { fetch, update } = useApiMethods();
    const navigate = useNavigate();

    const [rows,        setRows]        = useState([]);
    const [briefing,    setBriefing]    = useState(null);
    const [loading,     setLoading]     = useState(false);
    const [search,      setSearch]      = useState("");
    const [statusTab,   setStatusTab]   = usePersistedState("ai_sugg_status", "pending");
    const [actionFilter, setActionFilter] = usePersistedState("ai_sugg_action", null);
    const [users,          setUsers]          = useState([]);
    const [selectedUserId, setSelectedUserId] = usePersistedState("ai_sugg_user", "");
    const [savingId,    setSavingId]    = useState(null);
    const [openId,      setOpenId]      = useState(null);
    const [showBrief,   setShowBrief]   = usePersistedState("ai_sugg_show_brief", true);

    const load = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (statusTab)      params.append("status",  statusTab);
        if (selectedUserId) params.append("user_id", selectedUserId);
        const qs = params.toString();

        Promise.all([
            fetch(qs ? `${AI_SUGGESTIONS_URL}?${qs}` : AI_SUGGESTIONS_URL),
            fetch(AI_BRIEFING_URL),
        ])
            .then(([sugg, brief]) => {
                setRows(Array.isArray(sugg?.data) ? sugg.data : []);
                setBriefing(brief?.data || null);
                if (sugg?.status !== "success") {
                    ToastMsg("error", sugg?.message || "Failed to load suggestions");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load suggestions"))
            .finally(() => setLoading(false));
    }, [statusTab, selectedUserId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        fetch(USERS_SIMPLE_URL).then(res => {
            if (res?.status === "success") setUsers(res.data || []);
        }).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const actionCounts = useMemo(() => {
        const counts = {};
        rows.forEach(r => { counts[r.action_type] = (counts[r.action_type] || 0) + 1; });
        return counts;
    }, [rows]);

    const filtered = useMemo(() => {
        let out = rows;
        if (actionFilter) out = out.filter(r => r.action_type === actionFilter);
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            out = out.filter(r =>
                r.customer_name?.toLowerCase().includes(q) ||
                r.contact_number?.toLowerCase().includes(q) ||
                r.user_name?.toLowerCase().includes(q) ||
                r.reasoning?.toLowerCase().includes(q)
            );
        }
        return out;
    }, [rows, actionFilter, search]);

    const setStatus = (row, newStatus) => {
        setSavingId(row.id);
        update(AI_SUGGESTION_UPDATE(row.id), { status: newStatus })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", `Marked ${newStatus}`);
                    // Drop the row locally when it no longer matches the active tab
                    setRows(prev => statusTab === "all"
                        ? prev.map(r => (r.id === row.id ? { ...r, status: newStatus } : r))
                        : prev.filter(r => r.id !== row.id));
                } else {
                    ToastMsg("error", res?.message || "Failed to update");
                }
            })
            .catch(() => ToastMsg("error", "Failed to update"))
            .finally(() => setSavingId(null));
    };

    const copyScript = (text) => {
        navigator.clipboard?.writeText(text)
            .then(() => ToastMsg("success", "Script copied"))
            .catch(() => ToastMsg("error", "Could not copy"));
    };

    return (
        <ContainerCard>
            {/* ── header ── */}
            <div className="flex-shrink-0">
                <div className="flex items-center justify-between mb-4 px-1">
                    <div>
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-indigo-500" />
                            AI Next Actions
                        </h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            What to do next on each lead, ranked by urgency — written by the local AI worker
                        </p>
                    </div>
                    <Button onClick={load} size="sm" variant="outline"
                        className="flex items-center gap-1 text-xs" disabled={loading}>
                        <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
                        Refresh
                    </Button>
                </div>

                {/* ── manager briefing ── */}
                {briefing && (
                    <div className="mb-4 rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/20 overflow-hidden">
                        <button
                            onClick={() => setShowBrief(!showBrief)}
                            className="w-full flex items-center justify-between px-4 py-2.5 text-left"
                        >
                            <span className="flex items-center gap-2 text-xs font-semibold text-indigo-900 dark:text-indigo-200">
                                <Sparkles className="h-3.5 w-3.5" />
                                Manager Briefing · {dayjs(briefing.briefing_date).format("DD MMM YYYY")}
                                {briefing.metrics?.leads_analysed != null && (
                                    <span className="font-normal text-indigo-700/70 dark:text-indigo-300/70">
                                        ({briefing.metrics.leads_analysed} leads analysed
                                        {briefing.metrics.high_priority_count
                                            ? `, ${briefing.metrics.high_priority_count} urgent`
                                            : ""})
                                    </span>
                                )}
                            </span>
                            <ChevronDown className={cn("h-4 w-4 text-indigo-500 transition-transform",
                                showBrief && "rotate-180")} />
                        </button>
                        {showBrief && (
                            <div className="px-4 pb-4 pt-1 border-t border-indigo-200/60 dark:border-indigo-800/60">
                                <Briefing md={briefing.summary_md} />
                            </div>
                        )}
                    </div>
                )}

                {/* ── status tabs + rep filter ── */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <div className="flex items-center gap-1 rounded-lg border border-gray-200 dark:border-gray-700 p-0.5">
                        {STATUS_TABS.map(t => (
                            <button
                                key={t.value}
                                onClick={() => setStatusTab(t.value)}
                                className={cn(
                                    "px-2.5 py-1 rounded-md text-xs font-medium transition-colors",
                                    statusTab === t.value
                                        ? "bg-indigo-600 text-white"
                                        : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                )}
                            >{t.label}</button>
                        ))}
                    </div>

                    <div className="relative">
                        <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
                            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                        >
                            <option value="">All Reps</option>
                            {users.map(u => {
                                const name = [u.login_fname, u.login_lname].filter(Boolean).join(" ") || u.login_name;
                                return <option key={u.id} value={u.id}>{name}</option>;
                            })}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>

                    <div className="ml-auto flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1 bg-white dark:bg-gray-800">
                        <Search className="h-3 w-3 text-gray-400" />
                        <input
                            type="text" value={search} onChange={e => setSearch(e.target.value)}
                            placeholder="Customer, rep or reason…"
                            className="text-xs bg-transparent outline-none text-gray-700 dark:text-gray-300 w-40"
                        />
                    </div>
                </div>

                {/* ── action chips ── */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <button
                        onClick={() => setActionFilter(null)}
                        className={cn("px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                            !actionFilter
                                ? "bg-gray-700 text-white border-gray-800"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700")}
                    >All · {rows.length}</button>

                    {Object.keys(ACTIONS).filter(a => actionCounts[a]).map(a => {
                        const meta = ACTIONS[a];
                        const Icon = meta.icon;
                        return (
                            <button key={a}
                                onClick={() => setActionFilter(actionFilter === a ? null : a)}
                                className={cn("px-3 py-1 rounded-full text-xs font-medium border transition-colors flex items-center gap-1",
                                    actionFilter === a
                                        ? "bg-indigo-600 text-white border-indigo-700"
                                        : cn(meta.badge, "border-transparent hover:opacity-90"))}
                            >
                                <Icon className="h-3 w-3" />
                                {meta.label} · {actionCounts[a]}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── list ── */}
            {loading ? (
                <div className="flex items-center justify-center flex-1">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary mx-auto" />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading suggestions…</p>
                    </div>
                </div>
            ) : filtered.length === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-md px-6">
                        <Sparkles className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No suggestions here</p>
                        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                            {rows.length === 0
                                ? "Run the local worker to generate them: manage.py generate_lead_suggestions --tenant <slug>"
                                : "Nothing matches the current filters."}
                        </p>
                    </div>
                </div>
            ) : (
                // One scroll on this page, not two. The list grows to its full height
                // and ContainerCard scrolls it. It used to be flex-1 min-h-0 overflow-auto,
                // which made it a nested scroll region — and because min-h-0 lets a flex
                // child collapse to zero, a long Manager Briefing above squeezed it away
                // entirely with no way to scroll it back.
                <div className="space-y-2 pr-0.5">
                    {filtered.map(row => {
                        const meta = ACTIONS[row.action_type] || { label: row.action_type, icon: Sparkles, badge: "bg-gray-100 text-gray-600" };
                        const Icon = meta.icon;
                        const isOpen = openId === row.id;
                        return (
                            <div key={row.id}
                                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-indigo-300 dark:hover:border-indigo-700 transition-colors overflow-hidden">
                                <div className="flex">
                                    {/* priority rail — encodes urgency as position + colour, not just a number */}
                                    <div className={cn("w-1 flex-shrink-0", priorityTone(row.priority))} />

                                    <div className="flex-1 min-w-0 p-3">
                                        <div className="flex items-start justify-between gap-3 flex-wrap">
                                            <div className="min-w-0">
                                                <button
                                                    onClick={() => navigate("/customers/customerdetails", {
                                                        state: { formEdit: true, row: { id: row.customer_id } },
                                                    })}
                                                    className="text-sm font-medium text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 truncate max-w-[280px] text-left"
                                                >
                                                    {row.customer_name || "—"}
                                                </button>
                                                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 flex-wrap">
                                                    {row.lead_stage && <span>{row.lead_stage}</span>}
                                                    {row.contact_number && <><span>·</span><span>{row.contact_number}</span></>}
                                                    <span>·</span>
                                                    <span>{row.user_name || "Unassigned"}</span>
                                                    {row.generated_at && <><span>·</span>
                                                        <span>{dayjs(row.generated_at).format("DD MMM, hh:mm A")}</span></>}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-1", meta.badge)}>
                                                    <Icon className="h-3 w-3" />
                                                    {meta.label}
                                                </span>
                                                <span className="text-[10px] font-medium text-gray-500 dark:text-gray-400 tabular-nums">
                                                    {priorityLabel(row.priority)} · {row.priority}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                                            <TruncatedText text={row.reasoning} lines={2} label="Reasoning" />
                                        </div>

                                        {row.suggested_script && (
                                            <>
                                                <button
                                                    onClick={() => setOpenId(isOpen ? null : row.id)}
                                                    className="mt-2 text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
                                                >
                                                    <ChevronDown className={cn("h-3 w-3 transition-transform", isOpen && "rotate-180")} />
                                                    {isOpen ? "Hide" : "Show"} suggested opener
                                                </button>
                                                {isOpen && (
                                                    <div className="mt-1.5 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 p-2.5">
                                                        <p className="text-xs text-gray-700 dark:text-gray-200 whitespace-pre-line">
                                                            {row.suggested_script}
                                                        </p>
                                                        <button
                                                            onClick={() => copyScript(row.suggested_script)}
                                                            className="mt-2 text-[10px] font-medium text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center gap-1"
                                                        >
                                                            <Clipboard className="h-3 w-3" /> Copy
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* lifecycle actions — closing the loop is what makes AI-followed
                                            vs ignored conversion measurable later */}
                                        <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                                            {row.status === "pending" && (
                                                <>
                                                    <Button size="sm" variant="outline" disabled={savingId === row.id}
                                                        onClick={() => setStatus(row, "accepted")}
                                                        className="h-7 text-[11px] px-2.5 flex items-center gap-1">
                                                        <Check className="h-3 w-3" /> Accept
                                                    </Button>
                                                    <Button size="sm" variant="outline" disabled={savingId === row.id}
                                                        onClick={() => setStatus(row, "dismissed")}
                                                        className="h-7 text-[11px] px-2.5 flex items-center gap-1">
                                                        <X className="h-3 w-3" /> Dismiss
                                                    </Button>
                                                </>
                                            )}
                                            {row.status === "accepted" && (
                                                <Button size="sm" variant="outline" disabled={savingId === row.id}
                                                    onClick={() => setStatus(row, "done")}
                                                    className="h-7 text-[11px] px-2.5 flex items-center gap-1">
                                                    <Check className="h-3 w-3" /> Mark done
                                                </Button>
                                            )}
                                            {(row.status === "done" || row.status === "dismissed") && (
                                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                                                    {row.status}
                                                </span>
                                            )}
                                            {row.model_used && (
                                                <span className="ml-auto text-[10px] text-gray-300 dark:text-gray-600">
                                                    {row.model_used}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </ContainerCard>
    );
};

export default AISuggestions;
